/**
 * @ngdoc service
 * @name mx.shell.NavigationProviderService
 * @module mx.shell
 *
 * @description
 * Represents an interface for injection concrete implementation of the Navigation Items loading mechanism.
 */
import type { RoutingService } from '../App/services/mx-routing-service';
import type { NavigationItemsLoaderService } from './navigation-items-loader-service';
import { NavigationItem, NavigationItemParameters } from './model/navigation-item';

export class MxNavigationProviderService {
	static $inject = ['mx.shell.RoutingService', 'mx.shell.NavigationItemsLoaderService'];
	constructor(
		private readonly routingService: RoutingService,
		private readonly navigationItemsLoaderService: NavigationItemsLoaderService
	) {}

	/**
	 * @ngdoc method
	 * @async
	 * @name mx.shell.NavigationProviderService#load
	 *
	 * @description
	 * Loads navigation items. Should be implemented
	 *
	 *
	 * @returns {promise} Resolved as an array of {@ref mx.shell.NavigationItem navigation items}. See {@ref mx.shell.NavigationItem navigation item} for details
	 */
	public async load(forceLoad = false, loadOnly?: boolean) {
		const isDeepLinking = location.pathname.includes('/object-details/');

		if (isDeepLinking) {
			return [];
		}
		const navItems = await this.navigationItemsLoaderService.load(forceLoad);
		const currentNavItem = this.findCurrentNavItem(navItems);

		if (!loadOnly && currentNavItem) {
			this.routingService.go(currentNavItem.route, currentNavItem.parameters);
		}
		return navItems;
	}

	private getRoutingUrl(path: string, params?: NavigationItemParameters) {
		if (!params) {
			return path;
		}

		return path.replace(/:([\w\d]+)\??/g, (str, p1: keyof NavigationItemParameters) => (p1 in params ? (params[p1] as string) : str));
	}

	private findCurrentNavItem(navItems: NavigationItem[]): NavigationItem | undefined {
		if (!Array.isArray(navItems)) {
			return;
		}

		const url = location.href;

		for (const i in navItems) {
			if (navItems.hasOwnProperty(i)) {
				const item = navItems[i];

				if (item.routingItem && typeof item.routingItem === 'object' && item.routingItem.path) {
					const routingUrl = this.getRoutingUrl(item.routingItem.path, item.parameters);

					if (url.lastIndexOf(routingUrl) === url.length - routingUrl.length) {
						return item;
					}
				}

				if (Array.isArray(item.children)) {
					const curItem = this.findCurrentNavItem(item.children);

					if (curItem) {
						return curItem;
					}
				}
			}
		}

		return;
	}
}
